/**
 * Cookie / analytics consent banner for the player SPA.
 *
 * Shows once, on first visit, only when a real GA4 id is configured and no
 * prior choice exists. Accept loads GA4; Decline stores the refusal and the
 * site behaves identically. The choice persists in localStorage.
 *
 * Styling lives in consent.css (imported from main.tsx): solid panel, no
 * borders, no gradients, brand-coloured accept.
 */
import { useEffect, useState } from 'react'
import { getConsent, grantConsent, denyConsent, initAnalytics, isConfigured } from './analytics'

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Load analytics now if the visitor already opted in on a past visit.
    initAnalytics()
    // Reading localStorage is client-only, so the banner's visibility can
    // only be decided after mount. This setState-in-effect is intentional.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isConfigured() && getConsent() === null) setVisible(true)
  }, [])

  if (!visible) return null

  // Resolve the choice and let the app know — so anything that was waiting on the
  // consent decision (e.g. an invite's sign-up prompt) can proceed without
  // stacking a modal on top of this banner and making it inert/unclickable.
  const resolve = (grant: boolean) => {
    if (grant) grantConsent(); else denyConsent()
    setVisible(false)
    try { window.dispatchEvent(new Event('dzm-consent-resolved')) } catch { /* */ }
  }

  return (
    <div className="dzm-consent" role="dialog" aria-live="polite" aria-label="Analytics consent">
      <p className="dzm-consent-text">
        We use Google Analytics to see how the server list gets used. No ads, no data selling.
        Decline and everything works exactly the same.
      </p>
      <div className="dzm-consent-actions">
        <button type="button" className="dzm-consent-btn dzm-consent-decline" onClick={() => resolve(false)}>
          Decline
        </button>
        <button type="button" className="dzm-consent-btn dzm-consent-accept" onClick={() => resolve(true)}>
          Accept
        </button>
      </div>
    </div>
  )
}
